import { PackageEvidence } from '@cadstrom/cadstrom-shared';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';

import { firstFollowableLocator } from '#components/datasheet-popover/first-followable-locator.js';
import { useTRPC } from '#src/trpc.js';
import { RowEvidenceCard } from './row-evidence-card.js';
import { useShowInDatasheet } from './show-in-datasheet-context.js';
import { useCastLinkVote, usePendingLinkVoteAddresses } from './use-cast-link-vote.js';

import type { ReactNode } from 'react';
import type { EvidenceRowPick } from './evidence-row-selection-store.js';

const SKELETON_CARD_HEIGHT = 168;

const NO_ADDRESSES: ReadonlySet<string> = new Set();

/** Props for {@link SelectedRowEvidence}. */
export interface SelectedRowEvidenceProps {
	/** File-store identifier of the package's datasheet. */
	fileId: string;
	/** The package rows whose evidence to show. */
	selection: EvidenceRowPick;
	/** Whether the viewer may vote on the locators. */
	canVote: boolean;
	/** Whether rejected locators are listed; they are hidden behind a count otherwise. */
	showRejected: boolean;
	/** Called when the viewer clears the selection. */
	onClear: () => void;
	/**
	 * Whether pressing the heading shows the selection's first datasheet row that is not rejected in the datasheet,
	 * and pressing a card shows its row; the heading is plain text otherwise, and a card's preview enlarges on hover.
	 */
	clickShowsInDatasheet?: boolean;
}

/**
 * Whether a locator has been rejected as the source of the selected rows.
 *
 * @param entry - The locator and the verdict on it.
 * @returns True when the verdict is rejected.
 */
function isRejected(entry: PackageEvidence.RowEvidenceLocator): boolean {
	return entry.confidence.verdict === PackageEvidence.EvidenceConfidenceVerdict.Rejected;
}

/** The locators the viewer has voted on under one pick. */
interface VotedLocators {
	/** The pick the votes were cast under. */
	pick: EvidenceRowPick;
	/** The voted locators' addresses. */
	addresses: ReadonlySet<string>;
}

/** The locators the viewer has voted on under the current pick, and how to record a vote or forget a failed one. */
interface VotedLocatorsTracker {
	/** The voted locators' addresses; empty for a pick the viewer has not voted under. */
	addresses: ReadonlySet<string>;
	/** Records a vote on a locator under the current pick. */
	add: (address: string) => void;
	/** Forgets a vote on a locator under the given pick; a no-op once another pick has replaced it. */
	remove: (pick: EvidenceRowPick, address: string) => void;
}

/**
 * The locators the viewer has voted on since the current pick was made. A new pick starts with none.
 *
 * @param pick - The current pick.
 * @returns The voted locators, and how to record or forget a vote.
 */
function useVotedLocators(pick: EvidenceRowPick): VotedLocatorsTracker {
	const [voted, setVoted] = useState<VotedLocators>(() => ({ pick, addresses: NO_ADDRESSES }));
	return {
		addresses: voted.pick === pick ? voted.addresses : NO_ADDRESSES,
		add: (address) =>
			setVoted((current) => ({
				pick,
				addresses: new Set(current.pick === pick ? current.addresses : NO_ADDRESSES).add(address),
			})),
		remove: (votedPick, address) =>
			setVoted((current) => {
				if (current.pick !== votedPick) {
					return current;
				}
				const addresses = new Set(current.addresses);
				addresses.delete(address);
				return { pick: votedPick, addresses };
			}),
	};
}

/**
 * The datasheet locators linked as the source of the package rows the viewer selected, set apart from the rest of the
 * panel under the selection's title and subtitle, one card per locator, each with its verdict and the viewer's yes or
 * no on whether it is where the rows come from. A vote applies to every selected row. Rejected locators are hidden
 * behind a count unless asked for, and faded when shown, except one the viewer has voted on since the current
 * selection was made, which stays in place faded so the vote can be undone; a vote that fails to save does not count.
 * Voting on a locator blocks further votes on that locator until its vote is saved. When asked, pressing the heading
 * shows the selection's first datasheet row that is not rejected in the datasheet, and pressing a card shows its row.
 */
export function SelectedRowEvidence({
	fileId,
	selection,
	canVote,
	showRejected,
	onClear,
	clickShowsInDatasheet = false,
}: SelectedRowEvidenceProps) {
	const trpc = useTRPC();
	const { packageId, selections, title, subtitle } = selection;
	const evidenceQuery = useQuery(
		trpc.packageEvidence.rowEvidence.queryOptions({ packageId, selections }, { throwOnError: false }),
	);
	const castLinkVote = useCastLinkVote();
	const pendingAddresses = usePendingLinkVoteAddresses({ packageId, selections });
	const votedLocators = useVotedLocators(selection);
	const showInDatasheet = useShowInDatasheet();
	const firstRowLocator =
		clickShowsInDatasheet && evidenceQuery.data !== undefined ?
			firstFollowableLocator(evidenceQuery.data.locators)
		:	null;

	let content: ReactNode;
	if (evidenceQuery.isPending) {
		content = (
			<Skeleton variant='rounded' height={SKELETON_CARD_HEIGHT} aria-label='Loading row evidence' className='w-full' />
		);
	} else if (evidenceQuery.isError) {
		content = (
			<div className='flex flex-col items-start gap-2'>
				<p className='text-red-11'>Could not load this row’s evidence.</p>
				<Button variant='outlined' onClick={() => void evidenceQuery.refetch()}>
					<span>Retry</span>
				</Button>
			</div>
		);
	} else if (evidenceQuery.data.locators.length === 0) {
		content = <p className='text-gray-a9 italic'>No datasheet rows are linked to this row yet.</p>;
	} else {
		const { locators } = evidenceQuery.data;
		const shown = locators.filter(
			(entry) => showRejected || !isRejected(entry) || votedLocators.addresses.has(entry.locator.address),
		);
		const hiddenCount = locators.length - shown.length;
		content =
			shown.length === 0 ?
				<p className='text-gray-a9 italic'>Every linked datasheet row has been rejected.</p>
			:	<>
					{shown.map((entry) => (
						<RowEvidenceCard
							key={entry.locator.address}
							fileId={fileId}
							entry={entry}
							canVote={canVote}
							dimmed={isRejected(entry)}
							clickShowsInDatasheet={clickShowsInDatasheet}
							isVoting={pendingAddresses.has(entry.locator.address)}
							onVote={(polarity) => {
								const { address } = entry.locator;
								votedLocators.add(address);
								void castLinkVote
									.mutateAsync({ packageId, locatorAddress: address, selections, polarity })
									.catch(() => votedLocators.remove(selection, address));
							}}
						/>
					))}
					{hiddenCount > 0 && (
						<p className='text-gray-a9 italic'>
							{hiddenCount} rejected {hiddenCount === 1 ? 'row' : 'rows'} hidden.
						</p>
					)}
				</>;
	}

	const heading = (
		<div className='flex min-w-0 flex-col gap-1.5'>
			<h3 className='text-xs font-medium tracking-wide text-blue-11 uppercase'>Selected row</h3>
			<p title={title} className='line-clamp-2 leading-snug font-medium break-words text-gray-12 [text-box:initial]'>
				{title}
			</p>
			{subtitle !== null && (
				<p title={subtitle} className='line-clamp-2 text-sm leading-snug break-words text-gray-a10 [text-box:initial]'>
					{subtitle}
				</p>
			)}
		</div>
	);

	return (
		<section
			aria-label='Selected row evidence'
			className='flex flex-col gap-3 rounded-r-md border-l-2 border-blue-9 bg-blue-a2 py-3 pr-3 pl-2.5'
		>
			<header className='flex items-start justify-between gap-2'>
				{firstRowLocator === null ?
					heading
				:	<Tooltip title='Show in datasheet' describeChild>
						<ButtonBase
							onClick={() =>
								showInDatasheet({
									fileId,
									initialPageNumber: firstRowLocator.pageNumber,
									initialBoundingBoxes: firstRowLocator.boundingBoxes,
								})
							}
							className={clsx(
								'min-w-0 justify-start rounded text-left',
								'hover:bg-blue-a3 focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-blue-9',
							)}
						>
							{heading}
						</ButtonBase>
					</Tooltip>
				}
				<Tooltip title='Clear selection'>
					<IconButton aria-label='Clear selection' size='small' className='-my-1 shrink-0' onClick={onClear}>
						<CloseIcon fontSize='small' />
					</IconButton>
				</Tooltip>
			</header>
			{content}
		</section>
	);
}
